import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { TranslationUtils } from '../../utils';
import './EarlyCheckout.scss';
import { ImageCheckout } from '../../assets/images';
import { Button } from '..';
import tripService from '../../services/trip/tripService';
import segmentService from '../../services/segment/segmentService';
import { Reservation, Stay } from 'guest-data-types-ts-lib';
import { fetchTripError, setEarlyCheckoutFlag } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { StoreState } from '../../types/store.type';
import ddLogger from '../../logging/ddLogger';

interface EarlyCheckoutModalProps {
    reservation: Reservation;
    stay: Stay;
}

export const EarlyCheckoutModal: React.FC<EarlyCheckoutModalProps> = (props) => {
    const { reservation, stay } = props;
    const [isShowingEarlyCheckOutModalMain, setIsShowingEarlyCheckOutMain] = useState(true);
    const [isShowingEarlyCheckoutModalConfirm, setIsShowingEarlyCheckoutModalConfirm] = useState(false);
    const [isShowingEarlyCheckoutModalCancel, setIsShowingEarlyCheckoutModalCancel] = useState(false);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();

    const { shouldShowEarlyCheckOut } = useSelector((store: StoreState) => store.trip);

    const time = moment(stay.check_out_date_time).format('LT');

    const handleEarlyCheckoutModalClose = async () => {
        segmentService.trackEvent('Trip Portal Early Checkout', { action: 'Not Yet' });
        ddLogger.info(`Early Checkout, clicked on Not Yet. ReservationId: ${reservation.id}`);
        setIsShowingEarlyCheckOutMain(false);
        setIsShowingEarlyCheckoutModalCancel(true);
        setIsShowingEarlyCheckoutModalConfirm(false);
    };

    const handleEarlyCheckoutModalConfirm = async () => {
        try {
            setIsSubmitDisabled(true);
            setIsLoading(true);
            await tripService.notifyEarlyCheckout(reservation.id);
            segmentService.trackEvent('Trip Portal Early Checkout', { action: 'Checkout' });
            ddLogger.info(`Early Checkout, clicked on Checkout. ReservationId: ${reservation.id}`);
            setIsShowingEarlyCheckOutMain(false);
            setIsShowingEarlyCheckoutModalConfirm(true);
            setIsShowingEarlyCheckoutModalCancel(false);
        } catch (e) {
            dispatch(fetchTripError(e));
        }
    };

    const handleEarlyCheckoutDismiss = async () => {
        dispatch(setEarlyCheckoutFlag(false));
        segmentService.trackEvent('Trip Portal Early Checkout', { action: 'Dismiss' });
        ddLogger.info(`Early Checkout, clicked on Dismiss. ReservationId: ${reservation.id}`);
        setIsShowingEarlyCheckOutMain(false);
        setIsShowingEarlyCheckoutModalCancel(false);
        setIsShowingEarlyCheckoutModalConfirm(false);
    };

    const mainModal = () => {
        return (
            <React.Fragment>
                <div className="early-checkout-modal-text">
                    <h4 className={'heading-large'}>{TranslationUtils.get('ready_to_checkout')}</h4>
                    <div className="mb-4" />
                    <p className={'body-regular'}>{TranslationUtils.get('note_early_checkout')}</p>
                </div>
                <div className="early-checkout-modal-buttons">
                    <Button
                        id="button-confirm-checkout"
                        className="body-regular action-button"
                        isLoading={isLoading}
                        onClick={handleEarlyCheckoutModalConfirm}
                        disabled={isSubmitDisabled}
                        label={TranslationUtils.get('checkout_now')}
                        variant="primary"
                        block
                    />
                    <Button
                        id="button-cancel-checkout"
                        className="body-regular action-button"
                        onClick={handleEarlyCheckoutModalClose}
                        label={TranslationUtils.get('not_ready_yet')}
                        variant="default"
                        block
                    />
                </div>
            </React.Fragment>
        );
    };

    const cancelModal = () => {
        return (
            <React.Fragment>
                <div className="early-checkout-modal-text">
                    <h4>{TranslationUtils.get('got_it')}</h4>
                    <div className="mb-4" />
                    <p>{TranslationUtils.get('note_cancel_early_cehckout', { checkout: time })}</p>
                </div>
                <div className="early-checkout-modal-buttons">
                    <Button
                        id="button-cancel-checkout"
                        className="action-button"
                        onClick={handleEarlyCheckoutDismiss}
                        label={TranslationUtils.get('close')}
                        variant="default"
                        block
                    />
                </div>
            </React.Fragment>
        );
    };

    const confirmModal = () => {
        return (
            <React.Fragment>
                <div className="early-checkout-modal-text">
                    <h4>{TranslationUtils.get('all_set')}</h4>
                    <div className="mb-4" />
                    <p>{TranslationUtils.get('note_confirm_early_checkout')}</p>
                </div>
                <div className="early-checkout-modal-buttons">
                    <Button
                        id="button-cancel-checkout"
                        className="action-button"
                        onClick={handleEarlyCheckoutDismiss}
                        label={TranslationUtils.get('close')}
                        variant="default"
                        block
                    />
                </div>
            </React.Fragment>
        );
    };

    return (
        <Modal show={shouldShowEarlyCheckOut} onHide={handleEarlyCheckoutDismiss}>
            <Modal.Header closeButton />
            <div id="early-checkout-modal">
                {<ImageCheckout className="early-checkout-modal-image" />}
                {isShowingEarlyCheckOutModalMain && mainModal()}
                {isShowingEarlyCheckoutModalConfirm && confirmModal()}
                {isShowingEarlyCheckoutModalCancel && cancelModal()}
            </div>
        </Modal>
    );
};
